import { useCallback, useEffect, useState } from 'react';
import { useInjection } from 'inversify-react';
import { API } from '../services/HTTP';
import type { Tile } from '../components/TileDisplay';

const DASHBOARD_ENDPOINT = 'users/me/dashboard';

export interface TileLayout {
  id: string;
  title: string;
  dataSource: string;
  chartType: 'line' | 'bar' | 'single';
}

function parseLayouts(payload: unknown): TileLayout[] {
  const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload;
  return Array.isArray(parsed) ? (parsed as TileLayout[]) : [];
}

/**
 * Hook for persisting and loading dashboard tile layouts (not data).
 * The API stores only the tile configuration; data is fetched separately.
 */
export function useTilePersistence() {
  const api = useInjection<API>('API');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load the saved dashboard layout from the backend.
   * Returns tile configurations without data.
   */
  const load = useCallback(async (): Promise<TileLayout[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<TileLayout[]>(DASHBOARD_ENDPOINT);
      return parseLayouts(response.data);
    } catch (err) {
      console.error('Error loading dashboard layout:', err);
      setError('Failed to load dashboard layout. Please try again later.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [api]);

  /**
   * Save the dashboard layout (tile configurations) to the backend.
   * Only persists the layout, not the tile data.
   */
  const save = useCallback(
    async (layouts: TileLayout[]): Promise<boolean> => {
      setSaving(true);
      setError(null);
      try {
        await api.post(DASHBOARD_ENDPOINT, JSON.stringify(layouts));
        return true;
      } catch (err) {
        console.error('Error saving dashboard layout:', err);
        setError('Failed to save dashboard. Please try again later.');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [api]
  );

  return { load, save, loading, saving, error };
}
